import React, { Suspense, useCallback, useMemo } from "react";
import { commitMutation, graphql, QueryRenderer, type RelayRefetchProp, useFragment } from "react-relay";
import { Link } from "react-router-dom";
import type { OnChangeValue } from "react-select";

import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faBolt } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import cx from "classnames";
import { debounce } from "lodash";
import moment from "moment";
import { fetchQuery } from "relay-runtime";

import { useSyncedPersistentState } from "~/actionItems/hooks/useSyncedPersistentState";
import { zindexDropdown } from "~/css/zindexes";
import { asanaStore } from "~/integrations/asana/store";
import { ReactComponent as AsanaLogo } from "~/legacy-assets/images/logos/asana.svg";
import { promptAsanaAuth } from "~/lib/auth/index";
import { commitMutationPromise, smart, useLazyLoadQuery } from "~/lib/relayUtil";
import { plural } from "~/lib/utils";
import { environment } from "~/models";
import { Button, Checkbox } from "~/ui";
import { LoadingSpinner, toast } from "~/ui/";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import { Switch } from "~/ui/legacyFellowIndex";
import { IconButton, Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import fellowSelectProps from "~/ui/react-select";
import AsyncSelector from "~/ui/selectors/AsyncSelector";
import { toasty } from "~/ui/toast";

import Settings from "../../../account/Integrations/Settings";
import AsanaDisconnectModal from "./AsanaDisconnectModal";
import AsanaRemoveProjectModal from "./AsanaRemoveProjectModal";
import AboutAsana from "./AsanaText";
import { type WorkspaceOption, WorkspaceSelector } from "./WorkspaceSelector";

import type { AsanaIntegrationAddProjectAsyncTaskQuery } from "~/__generated__/AsanaIntegrationAddProjectAsyncTaskQuery.graphql";
import type { AsanaIntegrationAddProjectContent_viewer$key as AsanaAddProjectContentKey } from "~/__generated__/AsanaIntegrationAddProjectContent_viewer.graphql";
import type { AsanaIntegrationAddProjectItemMutation } from "~/__generated__/AsanaIntegrationAddProjectItemMutation.graphql";
import type { AsanaIntegrationAddProjectMutation as AddAsanaProjectType } from "~/__generated__/AsanaIntegrationAddProjectMutation.graphql";
import type { AsanaIntegrationPerItemModalWorkspaceQuery } from "~/__generated__/AsanaIntegrationPerItemModalWorkspaceQuery.graphql";
import type { AsanaIntegrationProjectsQuery } from "~/__generated__/AsanaIntegrationProjectsQuery.graphql";
import type { AsanaIntegrationQuery } from "~/__generated__/AsanaIntegrationQuery.graphql";
import type { AsanaIntegrationRemoveProjectItemMutation } from "~/__generated__/AsanaIntegrationRemoveProjectItemMutation.graphql";
import type { AsanaIntegrations_viewer$data as AsanaIntegrationViewer } from "~/__generated__/AsanaIntegrations_viewer.graphql";
import type { AsanaIntegrationSyncMutation as SyncToAsanaType } from "~/__generated__/AsanaIntegrationSyncMutation.graphql";
import type { AsanaIntegrationToggleAsanaUserIncludeAiMutation as AsanaToggleUserIncludeAiType } from "~/__generated__/AsanaIntegrationToggleAsanaUserIncludeAiMutation.graphql";
import type { AsanaIntegrationToggleSyncMutation as AsanaToggleSyncType } from "~/__generated__/AsanaIntegrationToggleSyncMutation.graphql";
import type { AsanaIntegrationWorkspaceQuery } from "~/__generated__/AsanaIntegrationWorkspaceQuery.graphql";

import settingsStyle from "~/settings/css/settings.module.css";

import styles from "./Asana.module.css";

type AsanaResponseType = NonNullable<AsanaIntegrationWorkspaceQuery["response"]["viewer"]["integrations"]["asana"]>;

export type AsanaWorkspaceType = NonNullable<AsanaResponseType["workspaces"]>[0];
export type AsanaWorkspaceListType = NonNullable<AsanaResponseType["workspaces"]>;
export type AsanaProjectType = NonNullable<
	AsanaIntegrationProjectsQuery["response"]["viewer"]["integrations"]["asana"]
>["projectsForWorkspace"][0];

const AddAsanaProjectItemMutation = graphql`
	mutation AsanaIntegrationAddProjectItemMutation($input: AddAsanaProjectItemInput!) {
		addAsanaProjectItem(input: $input) {
			asanaProject {
				id
				name
			}
			asyncResultId
		}
	}
`;

const RemoveAsanaProjectItemMutation = graphql`
	mutation AsanaIntegrationRemoveProjectItemMutation($input: RemoveAsanaProjectItemInput!) {
		removeAsanaProjectItem(input: $input) {
			deletedAsanaBlockLinkId
		}
	}
`;

export const AddAsanaProjectMutation = graphql`
	mutation AsanaIntegrationAddProjectMutation($input: AddAsanaProjectInput!) {
		addAsanaProject(input: $input) {
			ok
			asanaProject {
				id
				name
			}
			asyncResultId
		}
	}
`;

const ToggleAsanaProjectSyncMutation = graphql`
	mutation AsanaIntegrationToggleSyncMutation($input: ToggleAsanaProjectSyncInput!) {
		toggleAsanaProjectSync(input: $input) {
			ok
		}
	}
`;

const ToggleAsanaUserIncludeAiMutation = graphql`
	mutation AsanaIntegrationToggleAsanaUserIncludeAiMutation($input: ToggleAsanaUserIncludeAIInput!) {
		toggleAsanaUserIncludeAi(input: $input) {
			ok
		}
	}
`;

const SyncToAsanaMutation = graphql`
	mutation AsanaIntegrationSyncMutation($input: SyncToAsanaInput!) {
		syncToAsana(input: $input) {
			ok
			asyncResultId
		}
	}
`;

const AsyncTaskQuery = graphql`
	query AsanaIntegrationAddProjectAsyncTaskQuery($taskId: JSON!) {
		viewer {
			asyncTask(taskId: $taskId) {
				taskId
				state
				info
			}
		}
	}
`;

const AsanaProjectsQuery = graphql`
	query AsanaIntegrationProjectsQuery($query: String, $externalWorkspaceId: String!) {
		viewer {
			integrations {
				asana {
					projectsForWorkspace(query: $query, externalWorkspaceId: $externalWorkspaceId) {
						id
						name
						type
					}
				}
			}
		}
	}
`;

export const AsanaWorkspaceQuery = graphql`
	query AsanaIntegrationWorkspaceQuery {
		viewer {
			integrations {
				asana {
					workspaces {
						id
						externalId
						name
					}
				}
			}
		}
	}
`;

const AsanaPerItemWorkspaceQuery = graphql`
	query AsanaIntegrationPerItemModalWorkspaceQuery {
		viewer {
			...AsanaIntegrationAddProjectContent_viewer
			integrations {
				asana {
					isConnected
					workspaces {
						id
						externalId
						name
					}
				}
			}
		}
	}
`;

export interface ProjectOption {
	label: string;
	value: string;
}

export function getProjectsLoader(externalWorkspaceId: string) {
	return function loadProjects(query: string, callback: (options: ProjectOption[]) => void): void {
		fetchQuery<AsanaIntegrationProjectsQuery>(environment, AsanaProjectsQuery, { query, externalWorkspaceId })
			.toPromise()
			.then(data => {
				const projects = (data?.viewer.integrations.asana?.projectsForWorkspace ?? []).map(project => ({
					label: project.name,
					value: project.id,
				}));

				callback(projects);
			});
	};
}

function ProjectSelect(props: {
	forWorkspaceId: string;
	onChange: (value: OnChangeValue<ProjectOption, false>) => void;
	defaultValue?: ProjectOption;
	projectOptional?: boolean;
}) {
	const { forWorkspaceId, onChange, defaultValue, projectOptional } = props;
	const placeholder = `Select a project${projectOptional ? " - optional" : ""}`;

	const ellipses = {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		textAlign: "left",
	} as const;

	const getOptionsLoader = useCallback(() => debounce(getProjectsLoader(forWorkspaceId), 500), [forWorkspaceId]);

	return (
		<AsyncSelector
			{...fellowSelectProps}
			key={`asana-project-select-${forWorkspaceId}`}
			className={cx(fellowSelectProps.className, styles.select)}
			styles={{ option: base => ({ ...base, ...ellipses }), menuPortal: base => ({ ...base, zIndex: zindexDropdown }) }}
			onChange={onChange}
			loadOptions={getOptionsLoader()}
			defaultOptions
			placeholder={placeholder}
			isClearable
			hideSelectedOptions
			cacheOptions
			defaultValue={defaultValue}
			menuPortalTarget={document.body}
			menuPosition="fixed"
		/>
	);
}

interface AddProject {
	selectedProject?: AsanaProjectType;
	selectedWorkspace?: AsanaWorkspaceType;
	onSelectedWorkspaceChange: (value: string) => void;
	onSelectedProjectChange: (value: OnChangeValue<ProjectOption, false>) => void;
	onActionItemSyncedChanged?: (checked: boolean) => void;
	viewer: AsanaAddProjectContentKey;
}

interface AddProjectContentProps extends AddProject {
	projectOptional?: boolean;
}

export const AddProjectContent = ({ ...props }: AddProjectContentProps) => {
	const user = useFragment(
		graphql`
			fragment AsanaIntegrationAddProjectContent_viewer on Viewer {
				integrations {
					asana {
						workspaces {
							id
							externalId
							name
						}
					}
				}
			}
		`,
		props.viewer,
	);

	const { asana } = user.integrations;

	const workspaces = React.useMemo(() => {
		return asana?.workspaces?.map(workspace => ({
			label: workspace.name,
			value: workspace.externalId,
		}));
	}, [asana]);

	const defaultProjectOption = props.selectedProject
		? { label: props.selectedProject.name, value: props.selectedProject.id }
		: undefined;
	return (
		<>
			<WorkspaceSelector
				value={props.selectedWorkspace?.externalId}
				className={styles.projectSelector}
				options={workspaces || []}
				onChange={props.onSelectedWorkspaceChange}
			/>
			{props.selectedWorkspace && (
				<ProjectSelect
					forWorkspaceId={props.selectedWorkspace.id}
					onChange={props.onSelectedProjectChange}
					defaultValue={defaultProjectOption}
					projectOptional={props.projectOptional}
				/>
			)}
		</>
	);
};

interface AddProjectProps extends AddProject {
	actionItemCount?: number;
	actionItemSyncedChecked?: boolean;
	onClose: () => void;
	onAddProject?: () => void;
	onActionItemSyncedChanged?: (checked: boolean) => void;
	includeAiGeneratedChecked: boolean;
	onIncludeAiGeneratedChanged: (checked: boolean) => void;
}

export const AddProjectModal: React.FunctionComponent<AddProjectProps> = props => {
	const s = props.actionItemCount ? plural(props.actionItemCount) : "";

	return (
		<Modal onClose={props.onClose} className={styles.addProjectModal}>
			<Modal.Header onClose={props.onClose}>
				<Modal.Title>
					<AsanaLogo className={styles.logo} /> Select an Asana Project
				</Modal.Title>
			</Modal.Header>
			<Modal.Body dark className={styles.addProjectBody}>
				<AddProjectContent {...props} />
				{props.actionItemCount != 0 && (
					<>
						<div className={styles.actionItemOption}>
							<Checkbox
								checked={props.actionItemSyncedChecked}
								onCheckedChange={props.onActionItemSyncedChanged}
								className={styles.checkIcon}
							/>
							<span>
								Create new Asana task{s} for <b>{props.actionItemCount}</b> incomplete action item{s} in Fellow.
							</span>
						</div>
						{props.actionItemSyncedChecked && (
							<div className={styles.actionItemOption}>
								<Checkbox
									checked={props.includeAiGeneratedChecked}
									onCheckedChange={props.onIncludeAiGeneratedChanged}
									className={styles.checkIcon}
								/>
								<span>Include AI-generated action items</span>
							</div>
						)}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={props.onClose}>
					Cancel
				</Button>
				<Button
					size="normal"
					variant="primary"
					disabled={props.selectedWorkspace == undefined}
					onClick={props.onAddProject}
				>
					Add
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

interface ChangeProjectModalProps {
	workspaces: WorkspaceOption[];
	actionItemCount?: number;
	actionItemSyncedChecked: boolean;
	onClose: () => void;
	selectedProject?: AsanaProjectType;
	selectedWorkspace?: AsanaWorkspaceType;
	onChangeProject: () => void;
	onSelectedWorkspaceChange: (value: string) => void;
	onSelectedProjectChange: (value: OnChangeValue<ProjectOption, false>) => void;
	onActionItemSyncedChanged: (checked: boolean) => void;
	includeAiGeneratedChecked: boolean;
	onIncludeAiGeneratedChanged: (checked: boolean) => void;
}

const ChangeProjectModal: React.FunctionComponent<ChangeProjectModalProps> = props => {
	const defaultProjectOption = props.selectedProject
		? { label: props.selectedProject.name, value: props.selectedProject.id }
		: undefined;

	const s = props.actionItemCount ? plural(props.actionItemCount) : "";

	return (
		<Modal onClose={props.onClose} className={styles.addProjectModal}>
			<Modal.Header onClose={props.onClose}>
				<Modal.Title>
					<AsanaLogo className={styles.logo} /> Change Asana Project
				</Modal.Title>
			</Modal.Header>
			<Modal.Body dark className={styles.addProjectBody}>
				<WorkspaceSelector
					value={props.selectedWorkspace?.externalId}
					className={styles.projectSelector}
					options={props.workspaces || []}
					onChange={props.onSelectedWorkspaceChange}
				/>
				{props.selectedWorkspace && (
					<ProjectSelect
						forWorkspaceId={props.selectedWorkspace.id}
						onChange={props.onSelectedProjectChange}
						defaultValue={defaultProjectOption}
					/>
				)}
				{props.actionItemCount != 0 && (
					<>
						<div className={styles.actionItemOption}>
							<Checkbox
								checked={props.actionItemSyncedChecked}
								onCheckedChange={props.onActionItemSyncedChanged}
								className={styles.checkIcon}
							/>
							<span>
								Create new Asana task{s} for <b>{props.actionItemCount}</b> incomplete action item{s} in Fellow.
							</span>
						</div>
						{props.actionItemSyncedChecked && (
							<div className={styles.actionItemOption}>
								<Checkbox
									checked={props.includeAiGeneratedChecked}
									onCheckedChange={props.onIncludeAiGeneratedChanged}
									className={styles.checkIcon}
								/>
								<span>Include AI-generated action items</span>
							</div>
						)}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={props.onClose}>
					Cancel
				</Button>
				<Button
					size="normal"
					variant="primary"
					disabled={props.selectedWorkspace == undefined}
					onClick={props.onChangeProject}
				>
					Update
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export interface AsanaPerItemModalProps {
	userId: string;
	blockKey: string;
	noteId: string;
	isAsanaConnected: boolean;
	initialText?: string;
}

export function AsanaPerItemModal({
	userId,
	blockKey,
	noteId,
	isAsanaConnected,
	close,
	initialText,
}: AsanaPerItemModalProps & { close: (result: boolean) => void }) {
	const cancel = React.useCallback(() => close(false), [close]);

	return (
		<Modal onClose={cancel} className={cx(styles.addProjectModal, styles.linkItemModalWidth)}>
			<Modal.Header onClose={cancel}>
				<Modal.Title>
					<AsanaLogo className={styles.logo} /> Asana
				</Modal.Title>
			</Modal.Header>
			<Suspense
				fallback={
					<Modal.Body className={styles.modalBodySpinner}>
						<LoadingSpinner dimmer={false} />
					</Modal.Body>
				}
			>
				<AsanaPerItemModalBody
					close={close}
					userId={userId}
					blockKey={blockKey}
					noteId={noteId}
					isAsanaConnected={isAsanaConnected}
					initialText={initialText}
				/>
			</Suspense>
		</Modal>
	);
}

export const removeAsanaPerItem = ({
	blockKey,
	noteId,
	close,
}: {
	blockKey: string;
	noteId: string;
	close: () => void;
}) => {
	commitMutation<AsanaIntegrationRemoveProjectItemMutation>(environment, {
		mutation: RemoveAsanaProjectItemMutation,
		variables: {
			input: {
				blockKey: blockKey,
				noteId: noteId,
			},
		},
		onCompleted: ({ removeAsanaProjectItem: removeAsanaBlockLink }) => {
			if (removeAsanaBlockLink) {
				toasty.success("Removed Asana from action item");
				close();
			} else {
				toasty.error("Failed to remove project from action item");
			}
		},
	});
};

export const AsanaPerItemModalBody: React.FunctionComponent<
	AsanaPerItemModalProps & { close: (result: boolean) => void }
> = props => {
	const [selectedProject, setSelectedProject] = useSyncedPersistentState<AsanaProjectType | undefined>(
		asanaStore,
		"selectedProject",
		undefined,
	);
	const [selectedWorkspaceId, setSelectedWorkspaceId] = useSyncedPersistentState<string | undefined>(
		asanaStore,
		"selectedWorkspaceId",
		undefined,
	);

	const { blockKey, noteId, close, initialText } = props;

	const data = useLazyLoadQuery<AsanaIntegrationPerItemModalWorkspaceQuery>(
		AsanaPerItemWorkspaceQuery,
		{},
		{ fetchPolicy: "network-only" },
	);

	const workspaces = data.viewer.integrations.asana?.workspaces;

	const selectedWorkspace = useMemo(() => {
		if (selectedWorkspaceId && workspaces) {
			return workspaces.find(workspace => workspace.externalId == selectedWorkspaceId);
		}
		return undefined;
	}, [selectedWorkspaceId, workspaces]);

	const onSelectedWorkspaceChange = React.useCallback(
		(value: string) => {
			setSelectedWorkspaceId(value);
			setSelectedProject(undefined);
		},
		[setSelectedWorkspaceId, setSelectedProject],
	);

	const onSelectedProjectChange = React.useCallback(
		(option: OnChangeValue<ProjectOption, false>) => {
			if (option) {
				setSelectedProject({ id: option.value, name: option.label, type: "project" });
			} else {
				setSelectedProject(undefined);
			}
		},
		[setSelectedProject],
	);

	const onAddProjectToItem = React.useCallback(() => {
		const project = selectedProject ? selectedProject : selectedWorkspace;
		if (!project) return;

		return commitMutationPromise<AsanaIntegrationAddProjectItemMutation>(environment, {
			mutation: AddAsanaProjectItemMutation,
			variables: {
				input: {
					id: project.id,
					name: project.name,
					projectType: selectedProject ? "project" : "workspace",
					blockKey: blockKey,
					noteId: noteId,
					title: initialText,
				},
			},
		}).then(response => {
			if (response?.addAsanaProjectItem?.asanaProject?.id) {
				toasty.success(`Action item sent to Asana project ${response.addAsanaProjectItem?.asanaProject.name}`);
				close(true);
			}
			close(false);
		});
	}, [selectedProject, selectedWorkspace, blockKey, noteId, close, initialText]);

	const cancel = React.useCallback(() => close(false), [close]);

	if (!data) return <></>;

	return (
		<>
			<Modal.Body className={styles.addProjectBody}>
				<p>
					Choose an Asana workspace and project where <b>this action item</b> will be sent
				</p>
				<AddProjectContent
					onSelectedProjectChange={onSelectedProjectChange}
					onSelectedWorkspaceChange={onSelectedWorkspaceChange}
					selectedProject={selectedProject}
					selectedWorkspace={selectedWorkspace}
					viewer={data.viewer}
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={cancel}>
					Cancel
				</Button>
				{selectedWorkspace && selectedProject && (
					<Button size="normal" variant="primary" onClick={onAddProjectToItem}>
						Link to Asana
					</Button>
				)}
			</Modal.Footer>
		</>
	);
};

interface AsanaSettingsState {
	tab: string;
	selectedProject?: AsanaProjectType;
	selectedWorkspace?: AsanaWorkspaceType;
	connectProjectModalOpen: boolean;
	changeProjectModalOpen: boolean;
	taskId: string | null;
	syncPending: boolean;
	syncActionItems: boolean;
	workspaces?: AsanaWorkspaceListType;
}

export type AsanaSettingsProps = {
	onBackClick?: () => void;
};

type AsanaSettingsInternalProps = AsanaSettingsProps & {
	viewer: AsanaIntegrationViewer;
	relay: RelayRefetchProp;
};

class AsanaSettings extends React.Component<AsanaSettingsInternalProps, AsanaSettingsState> {
	static Fragments = {
		viewer: graphql`
			fragment AsanaIntegrations_viewer on Viewer {
				account {
					billingAccount {
						hasAccessToAsanaIntegration: hasAccessToFeature(feature: asana_integration)
					}
				}
				integrations {
					asana {
						id
						isConnected
						email
						connectedProject {
							id
							externalId
							workspaceExternalId
							name
							type
							syncEnabled
						}
						includeAiGenerated
						unsyncedActionItemCount
						syncedActionItemCount
						lastSyncDate
					}
					userLevelActionItemIntegration {
						id
						integrationProvider
					}
				}
				...AsanaIntegrationAddProjectContent_viewer
			}
		`,
	};

	static Refetch = graphql`
		query AsanaIntegrationsQuery {
			viewer {
				...AsanaIntegrations_viewer
			}
		}
	`;

	constructor(props: AsanaSettingsInternalProps) {
		super(props);

		this.state = {
			tab: "settings",
			connectProjectModalOpen: false,
			changeProjectModalOpen: false,
			taskId: null,
			syncPending: false,
			syncActionItems: true,
		};
	}

	componentDidMount() {
		this.loadWorkspaces();
	}

	loadWorkspaces = () => {
		fetchQuery<AsanaIntegrationWorkspaceQuery>(environment, AsanaWorkspaceQuery, {})
			.toPromise()
			.then(response => {
				const project = this.props.viewer.integrations.asana?.connectedProject;
				const workspaces = response?.viewer.integrations.asana?.workspaces;
				let currentWorkspace, currentProject;

				if (project) {
					if (project.type == "workspace") {
						workspaces?.forEach(function (workspace) {
							if (workspace.externalId == project.externalId) currentWorkspace = workspace;
						});
					} else {
						currentProject = project;
						workspaces?.forEach(function (workspace) {
							if (workspace.externalId == project.workspaceExternalId) currentWorkspace = workspace;
						});
					}
				}

				this.setState({
					selectedProject: currentProject,
					selectedWorkspace: currentWorkspace,
				});

				if (workspaces) {
					this.setState({
						workspaces: workspaces,
					});
				}
			});
	};

	refreshStateTimer: number | null = null;

	refreshState = () => {
		if (!this.state.taskId) return;

		fetchQuery<AsanaIntegrationAddProjectAsyncTaskQuery>(environment, AsyncTaskQuery, {
			taskId: this.state.taskId,
		})
			.toPromise()
			.then(result => {
				if (!result?.viewer.asyncTask) return;

				if (result.viewer.asyncTask.state == "SUCCESS") {
					this.props.relay.refetch({});
					this.setState({ syncPending: false });
					if (this.refreshStateTimer) clearTimeout(this.refreshStateTimer);
				} else {
					this.refreshStateTimer = window.setTimeout(this.refreshState, 10000);
				}
			});
	};

	getTabs = () => {
		if (this.props.viewer.integrations.asana?.isConnected) {
			return [
				{
					label: "Settings",
					key: "settings",
				},
				{
					label: "Getting started",
					key: "about",
				},
			];
		}

		return [];
	};

	onTabClick = (tab: string) => {
		this.setState({ tab });
	};

	onDisconnectClick = async () => {
		const result = await __globalModalContext?.open<boolean>(({ close }) => <AsanaDisconnectModal onClose={close} />);

		if (!result) return;
		this.props.relay.refetch({});
	};

	onChangeProjectModalOpen = () => {
		this.setState({ changeProjectModalOpen: true });
	};

	onRemoveProjectModalOpen = async () => {
		const project = this.props.viewer.integrations.asana?.connectedProject;

		if (!project) return;

		const result = await __globalModalContext?.open<boolean>(({ close }) => (
			<AsanaRemoveProjectModal onClose={close} project={project} />
		));

		if (!result) return;
		this.props.relay.refetch({});
	};

	connectProject = () => {
		this.setState({ connectProjectModalOpen: true });
	};

	onAddProjectModalClose = () => {
		this.setState({
			connectProjectModalOpen: false,
		});
	};

	onChangeProjectModalClose = () => {
		this.setState({
			changeProjectModalOpen: false,
		});
	};

	onSelectedWorkspaceChange = (value: string) => {
		const workspaces = this.state.workspaces;

		this.setState({
			selectedWorkspace: workspaces?.find(workspace => workspace.externalId == value),
			selectedProject: undefined,
		});
	};

	onSelectedProjectChange = (option: OnChangeValue<ProjectOption, false>) => {
		if (option) {
			this.setState({ selectedProject: { id: option.value, name: option.label, type: "project" } });
		} else {
			this.setState({ selectedProject: undefined });
		}
	};

	onActionItemSyncedChange = (checked: boolean) => {
		this.setState({ syncActionItems: checked });
	};

	handleIncludeAiGeneratedChange = (checked: boolean) => {
		commitMutation<AsanaToggleUserIncludeAiType>(environment, {
			mutation: ToggleAsanaUserIncludeAiMutation,
			variables: {
				input: {
					includeAi: checked,
				},
			},
			onCompleted: () => {
				this.props.relay.refetch({});
			},
		});
	};

	handleSyncEnabledChange = (checked: boolean) => {
		const project = this.props.viewer.integrations.asana?.connectedProject;

		if (!project) return;

		commitMutation<AsanaToggleSyncType>(environment, {
			mutation: ToggleAsanaProjectSyncMutation,
			variables: {
				input: {
					syncToProject: checked,
				},
			},
			onCompleted: () => {
				this.props.relay.refetch({});
			},
		});
	};

	onSyncActionItemsClick = () => {
		commitMutation<SyncToAsanaType>(environment, {
			mutation: SyncToAsanaMutation,
			variables: {
				input: {},
			},
			onCompleted: ({ syncToAsana }) => {
				if (syncToAsana?.ok) {
					this.props.relay.refetch({});
					if (syncToAsana.asyncResultId) {
						this.setState(
							{
								taskId: syncToAsana.asyncResultId,
							},
							() => {
								this.setState({ syncPending: true });
								this.refreshStateTimer = window.setTimeout(this.refreshState, 10000);
							},
						);
					}
				}
			},
		});
	};

	onAddProject = () => {
		const { selectedProject, selectedWorkspace } = this.state;

		const project = selectedProject ? selectedProject : selectedWorkspace;

		if (!project) return;

		commitMutation<AddAsanaProjectType>(environment, {
			mutation: AddAsanaProjectMutation,
			variables: {
				input: {
					id: project.id,
					name: project.name,
					projectType: selectedProject ? "project" : "workspace",
					syncItems: this.state.syncActionItems,
				},
			},
			onCompleted: ({ addAsanaProject }) => {
				if (addAsanaProject?.ok) {
					this.props.relay.refetch({});
					toast.success(`Added project "${project.name}"`);
					if (this.state.syncActionItems) {
						if (addAsanaProject.asyncResultId) {
							this.setState(
								{
									taskId: addAsanaProject.asyncResultId,
								},
								() => {
									this.setState({ syncPending: true });
									this.refreshStateTimer = window.setTimeout(this.refreshState, 10000);
								},
							);
						}
					}
				} else {
					toast.error(`Failed to add project "${project.name}"`);
				}
			},
		});
		this.onAddProjectModalClose();
		this.onChangeProjectModalClose();
	};

	renderFirstProjectPage = () => {
		const workspaces = this.state.workspaces;
		// todo: when more user level integrations are added, find a better way to do this generally
		const otherUserLevelIntegration = this.props.viewer.integrations.userLevelActionItemIntegration;
		let otherIntegrationPathName = "";
		let otherIntegrationNameAndType = "";

		if (otherUserLevelIntegration?.integrationProvider === "MICROSOFT_TODO") {
			otherIntegrationPathName = "microsoft-todo";
			otherIntegrationNameAndType = "Microsoft To Do list";
		}

		const includeAiGeneratedChecked = this.props.viewer.integrations.asana?.includeAiGenerated ?? true;

		return (
			<div className={styles.firstProjectPage}>
				{this.state.connectProjectModalOpen && workspaces && (
					<AddProjectModal
						includeAiGeneratedChecked={includeAiGeneratedChecked}
						onIncludeAiGeneratedChanged={this.handleIncludeAiGeneratedChange}
						actionItemCount={this.props.viewer.integrations.asana?.unsyncedActionItemCount}
						onClose={this.onAddProjectModalClose}
						onSelectedWorkspaceChange={this.onSelectedWorkspaceChange}
						onSelectedProjectChange={this.onSelectedProjectChange}
						selectedProject={this.state.selectedProject}
						selectedWorkspace={this.state.selectedWorkspace}
						onAddProject={this.onAddProject}
						actionItemSyncedChecked={this.state.syncActionItems}
						onActionItemSyncedChanged={this.onActionItemSyncedChange}
						viewer={this.props.viewer}
					/>
				)}
				<AsanaLogo className={styles.logo} />
				<h4>Select an Asana project</h4>
				<Button
					size="wide"
					variant="primary"
					onClick={this.connectProject}
					disabled={workspaces === undefined || otherUserLevelIntegration !== null}
				>
					Select a project {workspaces === undefined && <LoadingSpinner inline dimmer={false} />}
				</Button>
				{otherUserLevelIntegration && (
					<p className={styles.selectProjectDisabledText}>
						Note: You can only have one user level action item integration connected at a given time.
						<br />
						<Link to={`/settings/integrations/${otherIntegrationPathName}`}>
							<span>Remove your {otherIntegrationNameAndType} to continue.</span>
						</Link>
					</p>
				)}
			</div>
		);
	};

	renderProjectDefinedPage = () => {
		const project = this.props.viewer.integrations.asana?.connectedProject;
		const syncToProject = this.props.viewer.integrations.asana?.connectedProject?.syncEnabled ? true : false;

		const includeAiGenerated = this.props.viewer.integrations.asana?.includeAiGenerated ?? true;
		if (!project) return;

		const projectURL = `https://app.asana.com/0/${project.externalId}`;

		const unsyncedActionItemCount = this.props.viewer.integrations.asana?.unsyncedActionItemCount;
		const syncedActionItemCount = this.props.viewer.integrations.asana?.syncedActionItemCount;

		return (
			<>
				{this.state.changeProjectModalOpen && (
					<ChangeProjectModal
						workspaces={this.getWorkspaceDropdownOptions()}
						actionItemCount={unsyncedActionItemCount}
						onClose={this.onChangeProjectModalClose}
						onSelectedWorkspaceChange={this.onSelectedWorkspaceChange}
						onSelectedProjectChange={this.onSelectedProjectChange}
						selectedProject={this.state.selectedProject}
						selectedWorkspace={this.state.selectedWorkspace}
						onChangeProject={this.onAddProject}
						actionItemSyncedChecked={this.state.syncActionItems}
						onActionItemSyncedChanged={this.onActionItemSyncedChange}
						includeAiGeneratedChecked={includeAiGenerated}
						onIncludeAiGeneratedChanged={this.handleIncludeAiGeneratedChange}
					/>
				)}
				<div className={styles.projectHeader}>
					<div className={styles.projectTitle}>
						<AsanaLogo />
						<a href={projectURL} target="_blank" rel="noopener noreferrer" className={styles.headerText}>
							{project.name}
						</a>
						{this.state.syncPending && <div className={styles.connectionStatusLabel}>Sync in progress…</div>}
						{!this.state.syncPending && syncToProject && (
							<div className={cx(styles.connectionStatusLabel, styles.activeLabel)}>Active</div>
						)}
						{!this.state.syncPending && !syncToProject && (
							<div className={cx(styles.connectionStatusLabel, styles.inactiveLabel)}>Inactive</div>
						)}
					</div>
					{!this.state.syncPending && (
						<div className={styles.optionsContainer}>
							<div className={styles.toggleOptionContainer}>
								<div className={cx(styles.toggleOption, styles.syncToggleOption)}>
									<Switch
										size="small"
										id="sync-enabled"
										checked={syncToProject}
										onCheckedChange={this.handleSyncEnabledChange}
										className={cx(settingsStyle.settingsPageToggle, styles.toggle)}
									/>
									<span className={styles.toggleOptionDescription}>Sync action items</span>
								</div>

								{syncToProject && (
									<div className={styles.nestedToggleOption}>
										<span className={styles.indentArrowImage} />
										<Switch
											size="small"
											id="include-ai-generated"
											checked={includeAiGenerated}
											disabled={!syncToProject}
											className={cx(settingsStyle.settingsPageToggle, styles.toggle)}
											onCheckedChange={this.handleIncludeAiGeneratedChange}
										/>
										<span className={styles.toggleOptionDescription}>Include AI-generated</span>
									</div>
								)}
							</div>

							<ArrowDropdown
								heading="Project actions"
								popupClassName={styles.flyoutMenu}
								trigger={<IconButton className={styles.projectMenu} title="More actions" icon={faEllipsisV} />}
							>
								<DropdownItem key="change" onClick={this.onChangeProjectModalOpen} className={styles.dropdownItem}>
									Change project…
								</DropdownItem>

								<DropdownItem key="disconnect" onClick={this.onRemoveProjectModalOpen} className={styles.dropdownItem}>
									Remove project…
								</DropdownItem>
							</ArrowDropdown>
						</div>
					)}
				</div>
				{!this.state.syncPending && (
					<div className={styles.syncSection}>
						<span>
							Manually sync <b>({unsyncedActionItemCount})</b> incomplete action items to Asana
						</span>
						<Button
							size="normal"
							variant="primary"
							disabled={!unsyncedActionItemCount || unsyncedActionItemCount == 0}
							onClick={this.onSyncActionItemsClick}
						>
							Sync
						</Button>
					</div>
				)}
				<div className={styles.userStats}>
					<ul>
						{this.state.syncPending ? (
							/* prettier-ignore */
							<li>Syncing <b>({unsyncedActionItemCount})</b>incomplete action item{plural(unsyncedActionItemCount ?? 0)}
                            {" "}with Asana. This may take a few moments.
                                                        </li>
						) : (
							<>
								{this.props.viewer.integrations.asana?.lastSyncDate && (
									<li>
										Last item sent to this Asana project:{" "}
										{moment(this.props.viewer.integrations.asana.lastSyncDate).format("dddd, MMMM Do [at] h:mm a")}
									</li>
								)}
								{
									<li>
										{syncedActionItemCount ?? 0} item{plural(syncedActionItemCount ?? 0)} sent to this Asana project to
										date
									</li>
								}
							</>
						)}
					</ul>
				</div>
			</>
		);
	};

	renderContent = () => {
		const connected = this.props.viewer.integrations.asana?.isConnected ? true : false;
		const connectedProject = this.props.viewer.integrations.asana?.connectedProject;

		if (!connected || this.state.tab == "about") {
			return <AboutAsana />;
		} else if (connectedProject) {
			return this.renderProjectDefinedPage();
		} else {
			return this.renderFirstProjectPage();
		}
	};

	getWorkspaceDropdownOptions = (): WorkspaceOption[] => {
		const asanaWorkspaces = this.state.workspaces;
		if (!asanaWorkspaces) return [];
		return asanaWorkspaces.map(workspace => {
			return {
				label: workspace.name,
				value: workspace.externalId,
			};
		});
	};

	openConnectModal = async () => {
		return promptAsanaAuth({ refresh: true })
			.then(() => {
				this.props.relay.refetch({});
				this.loadWorkspaces();
				return toast.success(`Connected Asana`);
			})
			.catch(function ({ message }) {
				return toast.error(message);
			});
	};

	render() {
		const { integrations, account } = this.props.viewer;
		const connected = Boolean(integrations.asana?.isConnected);
		const paywalled = !account?.billingAccount.hasAccessToAsanaIntegration;

		return (
			<React.Fragment>
				<Settings
					name="Asana"
					connected={connected}
					logo={<AsanaLogo />}
					paywalledButton={undefined}
					paywalledBanner={paywalled ? "fellow_pro_ai" : undefined}
					paywallFeature="asana_integration"
					installText="Connect"
					tabs={this.getTabs()}
					activeTab={this.state.tab}
					onTabClick={this.onTabClick}
					onInstall={this.openConnectModal}
					onDisconnect={this.onDisconnectClick}
					backPath="/settings/integrations/"
					installIcon={faBolt}
					{...this.props}
					showMoreActions={!paywalled}
				>
					{this.renderContent()}
				</Settings>
			</React.Fragment>
		);
	}
}

const SmartAsanaSettings = smart(AsanaSettings);

function AsanaIntegrationRenderer(props: AsanaSettingsProps) {
	return (
		<QueryRenderer<AsanaIntegrationQuery>
			environment={environment}
			query={graphql`
				query AsanaIntegrationQuery {
					viewer {
						...AsanaIntegrations_viewer
					}
				}
			`}
			variables={{}}
			render={useCallback(({ props: data }) => (data ? <SmartAsanaSettings {...data} {...props} /> : null), [props])}
		/>
	);
}

export default AsanaIntegrationRenderer;
